import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PixelTable,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from 'pixel-base'
import styled from 'styled-components'
import {
  getAllJournalEntries,
  getOpenPeriods,
  getTopBalance,
} from 'utils/api-routes-accounts'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatsStrip from '../../components/FinanceStatsStrip'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { AccountingPeriod, BalanceRow, JournalEntry } from '../../types/finance'

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`

const QuickAction = styled.button`
  border: 1px solid #dbe3f1;
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border-radius: 14px;
  padding: 16px;
  text-align: left;
  cursor: pointer;

  strong {
    display: block;
    color: #111827;
    margin-bottom: 6px;
  }

  span {
    color: #6b7280;
    font-size: 13px;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const Dashboard = () => {
  const navigate = useNavigate()

  const recentEntries = useFinanceObservable<JournalEntry[]>(() =>
    getAllJournalEntries({ limit: 5 }),
  )
  const topBalances = useFinanceObservable<BalanceRow[]>(() => getTopBalance())
  const periods = useFinanceObservable<AccountingPeriod[]>(() =>
    getOpenPeriods(),
  )

  const postedEntries = (recentEntries.data || []).filter(
    (entry) => (entry.status || entry.state) !== 'draft',
  )

  const stats = [
    {
      label: 'Posted Entries',
      value: `${postedEntries.length}`,
      tone: 'success' as const,
    },
    {
      label: 'Open Periods',
      value: `${(periods.data || []).length}`,
      tone: 'warning' as const,
    },
    { label: 'Top Accounts', value: `${(topBalances.data || []).length}` },
    { label: 'Base Currency', value: 'PKR' },
  ]

  return (
    <FinancePageShell
      title="Finance Dashboard"
      isLoading={
        recentEntries.loading || topBalances.loading || periods.loading
      }
    >
      <FinanceStatsStrip items={stats} />
      <QuickActions>
        <QuickAction onClick={() => navigate('/finance/journal-entries')}>
          <strong>New journal entry</strong>
          <span>Create and post manual ledger entries.</span>
        </QuickAction>
        <QuickAction onClick={() => navigate('/finance/expenses')}>
          <strong>Manage expenses</strong>
          <span>Review draft, submitted, approved, and paid expenses.</span>
        </QuickAction>
        <QuickAction onClick={() => navigate('/finance/accounts')}>
          <strong>Chart of accounts</strong>
          <span>
            Maintain account structure, posting flags, and control settings.
          </span>
        </QuickAction>
        <QuickAction onClick={() => navigate('/finance/periods')}>
          <strong>Periods</strong>
          <span>
            Close and reopen accounting periods with audit-safe controls.
          </span>
        </QuickAction>
      </QuickActions>
      <Grid>
        <Section>
          <SectionHeader>
            <SectionTitle>Recent Posted Entries</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <PixelTable
              columns={[
                {
                  name: 'Number',
                  selector: (row) => row.move_number || row.name || '--',
                },
                {
                  name: 'Journal',
                  selector: (row) => row.journal?.name || '--',
                },
                { name: 'Reference', selector: (row) => row.reference || '--' },
                {
                  name: 'Status',
                  selector: (row) => row.status || row.state || '--',
                },
              ]}
              data={postedEntries}
              pagination={false}
            />
          </SectionContent>
        </Section>
        <Section>
          <SectionHeader>
            <SectionTitle>Open Periods</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <PixelTable
              columns={[
                { name: 'Name', selector: (row) => row.name },
                { name: 'From', selector: (row) => row.date_start },
                { name: 'To', selector: (row) => row.date_end },
              ]}
              data={periods.data || []}
              pagination={false}
            />
          </SectionContent>
        </Section>
      </Grid>
    </FinancePageShell>
  )
}

export default Dashboard
